import { logger } from './logger';

/** Counts reported by GET /platforms/transactions/tax-calculation/status. */
export interface TaxCalculationStatus {
  pending?: number;
  failed?: number;
  calculated?: number;
  total?: number;
  failure_reasons?: Array<{ reason: string; count: number }>;
}

export type TaxCalculationOutcome = {
  status: 'nothing_to_do' | 'completed' | 'failed' | 'timeout' | 'error';
  message: string;
  pending?: number;
  failed?: number;
  calculated?: number;
};

interface RunOptions {
  companyId?: number | null;
  /** Starts the calculation (platformService.calculateAllTax by default). */
  start: (options: { background: boolean; companyId?: number }) => Promise<any>;
  /** Reads the company's status counts (platformService.getTaxCalculationStatus by default). */
  status: (companyId?: number) => Promise<TaxCalculationStatus>;
  /** How many times to ask for the status after starting, and how long to wait between asks. */
  maxPolls?: number;
  pollMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** A message the user can act on for a failed request - never just "please try again". */
export const taxCalculationErrorMessage = (error: any): string => {
  const response = error?.response;
  const detail = typeof response?.data?.detail === 'string' ? response.data.detail.trim() : '';
  if (!response) {
    return "We couldn't reach the tax service. Check your connection and try again.";
  }
  const status = Number(response.status || 0);
  if (status === 401) return 'Your session expired. Sign in again and retry the calculation.';
  if (status === 403) return detail || "You don't have access to this company's transactions.";
  if (status === 400 || status === 422) {
    return detail || 'The tax request was rejected. Check the selected company and try again.';
  }
  if (status === 429) return 'Too many requests right now. Wait a moment and try again.';
  if (status >= 500) {
    return detail || 'The tax service hit a server error. Nothing was changed; please try again in a moment.';
  }
  return detail || `Tax calculation failed (HTTP ${status}). Please try again.`;
};

/**
 * Start "Calculate All Tax" and explain what happened.
 *
 * The endpoint only acknowledges the work (it runs in the background), so a silent failure
 * used to look like success. This waits for the background job to reach a stable state and
 * returns a message for the page: how many transactions were updated, how many are still
 * pending, or which reason stopped them. It never throws; a failed request becomes
 * `status: 'error'` with a specific message.
 */
export async function runCalculateAllTax({
  companyId,
  start,
  status,
  maxPolls = 20,
  pollMs = 1500,
  sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
}: RunOptions): Promise<TaxCalculationOutcome> {
  let started: any;
  try {
    started = await start({ background: true, ...(companyId ? { companyId } : {}) });
  } catch (error) {
    logger.error('[TAX] Could not start tax calculation', error);
    return { status: 'error', message: taxCalculationErrorMessage(error) };
  }

  const processed = Number(started?.transactions_processed ?? 0);
  if (processed === 0) {
    return {
      status: 'nothing_to_do',
      message: 'Nothing to calculate — no transactions are waiting for tax.',
    };
  }

  let latest: TaxCalculationStatus = {};
  try {
    for (let attempt = 0; attempt < maxPolls; attempt += 1) {
      latest = (await status(companyId || undefined)) || {};
      if (Number(latest.pending ?? 0) === 0) break;
      if (attempt < maxPolls - 1) await sleep(pollMs);
    }
  } catch (error) {
    logger.error('[TAX] Could not read tax calculation status', error);
    return {
      status: 'error',
      message: taxCalculationErrorMessage(error),
    };
  }

  const failed = Number(latest.failed ?? 0);
  const pending = Number(latest.pending ?? 0);
  const calculated = Number(latest.calculated ?? 0);

  if (failed > 0) {
    const reason = latest.failure_reasons?.find((item) => item.reason)?.reason;
    const base = `Tax calculation finished, but ${plural(failed, 'transaction')} could not be calculated.`;
    return {
      status: 'failed',
      failed,
      pending,
      calculated,
      message: `${base}${reason ? ` Reason: ${reason}` : ''} Open All Transactions to review and retry them.`,
    };
  }

  if (pending > 0) {
    return {
      status: 'timeout',
      pending,
      failed,
      calculated,
      message: `Tax calculation is still running for ${plural(pending, 'transaction')}. Refresh in a minute to see the results.`,
    };
  }

  return {
    status: 'completed',
    failed,
    pending,
    calculated,
    message: calculated > 0
      ? `Tax calculation finished. ${plural(calculated, 'transaction')} now have tax.`
      : 'Tax calculation finished. All waiting transactions were processed.',
  };
}

export default runCalculateAllTax;
